import { getAllPDFs, getInfoChannelWidgets } from '@config'
import { ROUTES } from '@constants/app_routes'
import { routesWithoutSlideShow } from '@constants/routes_definitions'
import { toastError } from '@constants/toasts'
import type { PagesType, PageType } from '@containers/admin/types'
import Logger from '@logger'
import type { NullableDate, NullableTimer } from '@shared_types/global_types'
import { asArray } from '@shared_utils/array_utils'
import { callApi } from '@utils/call_api'
import { get, isString } from 'lodash'
import type { Location, NavigateFunction } from 'react-router-dom'
import { type Socket } from 'socket.io-client'
import Cookies from 'universal-cookie'

import { ovalModeActive, replayModeActive, tvModeActive } from './app_url_params'

const logger = Logger.create('RemoteControl')

const cookies = new Cookies(null, { path: '/' })

const hiddenPages = ['pincode', 'presentations']

const hiddenPagesInOvalMode = ['calendar', 'trip', 'crew']

export function isPageHidden (page: string) {
  return hiddenPages.includes(page)
}

export function snakeToCamel (str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

interface RemoteControlProps {
  navigate: NavigateFunction
  location: Location
}

let instance: RemoteControl

export interface RemoteControlMessage {
  command: string
}

export default class RemoteControl {
  props: RemoteControlProps
  settings = { inactivityTimeout: 10000, startDelay: 10000 }
  pages: PagesType | [] = []
  timer: NullableTimer = null
  playTimer: NullableTimer = null
  startDelayTimer: NullableTimer = null
  inactivityTimer: NullableTimer = null
  inactivityMode = false
  delayMode = false
  pageIndex = 0
  running = false
  startedTime: NullableDate = null
  remainingMinTime: number = 2000
  readonly socket: Socket
  readonly isTvModePlayer: boolean

  constructor (props: RemoteControlProps, socket: Socket) {
    this.socket = socket

    this.isTvModePlayer = cookies.get<boolean>('isTvModePlayer')

    let startTimeoutId: NodeJS.Timeout | null = null

    window.addEventListener('click', () => {
      if (startTimeoutId) {
        clearTimeout(startTimeoutId)
        startTimeoutId = null
      }

      if (!this.isTvModePlayer) {
        return
      }

      const time = this.settings.inactivityTimeout
      const seconds = time / 1000

      logger.verbose(`interacted with document; will pause now and auto-restart after ${seconds} seconds`)

      this.pause()
      document.querySelector('html')?.classList?.remove('tv-mode')
      this.props.navigate(window.location.pathname.replace('?', ''))

      startTimeoutId = setTimeout(() => {
        document.querySelector('html')?.classList?.add('tv-mode')

        const newPageIndex = this.pages.findIndex(p => {
          return isString(p.path) && window.location.pathname.startsWith(p.path)
        })

        if (newPageIndex > -1) {
          this.pageIndex = newPageIndex
        }

        logger.verbose(`restart after no interaction for ${seconds} seconds`)
        this.play()
        if (newPageIndex >= 0) {
          this.next()
        }
      }, time)
    })

    window.addEventListener('keypress', (e: KeyboardEvent) => {
      if (tvModeActive() && /body/i.test(get(e, 'target.nodeName') || '')) {
        if (e.key === 'd') {
          this.next()
        } else if (e.key === 'a') {
          this.prev()
        } else if (e.key === 'w') {
          this.play()
        } else if (e.key === 's') {
          this.pause()
        }
      }
    })

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    instance = this
    this.props = props
    this._init()
  }

  start () {
    if (!tvModeActive()) {
      logger.warn('will not start - not in TV mode')
      return
    }

    logger.debug('started')
    if (this.delayMode) {
      this._startDelay()
    } else {
      this._startReview()
    }
    if (this.inactivityMode) {
      this._startInactivityManager()
    }
  }

  stop () {
    logger.debug('stopped')
    this._stopTimers()
    if (this.inactivityMode) {
      this._stopInactivityManager()
    }
  }

  private _cancelPlayTimer () {
    if (this.playTimer) {
      clearTimeout(this.playTimer)
    }
  }

  play () {
    if (!this.running) {
      this._notifyPlayState(true)
      logger.debug('playing')
      const timeout = this._getRemainingTimeout()

      this.playTimer = setTimeout(() => {
        this._increment()
        this._startReview()
        this._cancelPlayTimer()
        this.playTimer = null
      }, timeout)
    }
  }

  pause () {
    this._notifyPlayState(false)
    if (this.running) {
      this._stopTimers()
    }
  }

  next () {
    this._stopTimers()
    this._increment()
    this._startReview()
  }

  prev () {
    this._stopTimers()
    this._setPageIndex(this.pageIndex - 1, 'prev')
    this._startReview()
  }

  predefinedCommand (command: string) {
    let nextIndex = this.pages.findIndex(({ remoteControlCommand }) => remoteControlCommand === command)
    this._notifyServer({ command, commandPageIndex: nextIndex })

    if (nextIndex === -1) {
      nextIndex = this.pages.findIndex(({ name }) => name === command)
      this._notifyServer({ command, namePageIndex: nextIndex })
    }

    if (nextIndex === -1) {
      this._notifyServer({ invalidCommand: command })
    } else {
      this._stopTimers()
      this._setPageIndex(nextIndex, command)
      this._startReview()
    }
  }

  commandChanged (command: string) {
    logger.verbose(`run command '${command}'`)

    if (command === 'play') {
      this.play()
    } else if (command === 'pause') {
      this.pause()
    } else if (command === 'next') {
      this.next()
    } else if (command === 'prev') {
      this.prev()
    } else {
      this.predefinedCommand(command)
    }
  }

  _init () {
    callApi('/api/userSettings')
      .then(data => {
        this.settings = data.settings

        const allPages = asArray<PageType>(data.allPages)
        const enabledPages = asArray<string>(data.enabledPages)

        this.pages = allPages
          .sort((p1, p2) => {
            const o1 = p1.orderNumber || 0
            const o2 = p2.orderNumber || 0
            return o1 - o2
          })
          .filter(page => {
            if (replayModeActive()) {
              return page.name === 'info'
            }
            const altName = page.name === 'yacht_page' ? 'yachtInformation' : page.name === 'mekkah' ? 'makkah' : snakeToCamel(page.name)
            const isHidden = isPageHidden(page.name) || isPageHidden(altName)
            const isEnabled = enabledPages.includes(page.name) || enabledPages.includes(altName)

            if (ovalModeActive() && hiddenPagesInOvalMode.includes(page.name)) {
              return false
            }

            return page.enabled && page.timeout !== 0 && !isHidden && isEnabled
          })
          .map(page => {
            let path: ROUTES | string = ROUTES[page.name.toUpperCase() as keyof typeof ROUTES] ?? ''

            if (path.endsWith('/:id')) {
              path = path.replace('/:id', '')
            }
            if (replayModeActive()) {
              return { ...page, path, enabled: true }
            }
            return { ...page, path }
          })

        this._addIndexException('info_channel')
        this._addIndexException('pdf')

        console.log('Initialised RemoteControl with pages', this.pages)
      })
      .catch(error => {
        toastError('No response from API')
        logger.error(`failed to fetch user settings: ${error}`)
      })
  }

  _exceptions (key: string) {
    if (key === 'info_channel') {
      const infoChannelTV = getInfoChannelWidgets('tv') || []
      return infoChannelTV.length
    } else if (key === 'pdf') {
      const pdfTV = getAllPDFs() || []
      return pdfTV.length
    } else {
      return -1
    }
  }

  _addIndexException (key: string) {
    const index = this.pages.findIndex(page => page.name === key)
    if (index !== -1) {
      const length = this._exceptions(key)
      if (length > 0) {
        const page = { ...this.pages[index] }
        const newPages = []

        for (let i = 0; i < length; i++) {
          const name = `${key}_${i}`
          const newPage = {
            ...page,
            name,
            remoteControlCommand: name,
            path: page.path?.replace(':index', String(i))
          }
          newPages.push(newPage)
        }
        this.pages.splice(index, 1, ...newPages)
      }
    }
    // return
  }

  _stopTimers () {
    if (this.running) {
      this._stopReview()
    }
    if (this.startDelayTimer) {
      this._stopDelay()
    }
  }

  private _setPageIndex (index: number, cause: string) {
    if (index >= this.pages.length || index < 0) {
      index = 0
    }
    this.pageIndex = index
    this._notifyServer({ pageIndex: index, cause })
  }

  _increment () {
    this._setPageIndex(this.pageIndex + 1, '_increment')
  }

  _notifyServer<T> (data: T) {
    this.socket.emit('player-status', data)
  }

  _notifyPlayState (playing: boolean) {
    this._notifyServer({ playing, paused: !playing })
  }

  private _updateReview () {
    this.startedTime = new Date()

    if (this.timer) {
      this._increment()
    }

    const currentPage = this.pages[this.pageIndex]
    this._changeRoute(currentPage?.path || '')
    this._notifyServer({ currentPage })

    if (this.timer) {
      clearTimeout(this.timer)
    }
    logger.debug('current page', currentPage)

    if (currentPage?.timeout) {
      this.timer = setTimeout(() => this._updateReview(), currentPage?.timeout)
    } else {
      logger.debug('will not auto-advance')
    }
  }

  _startReview () {
    if (this._isSlideShowPossible()) {
      this.running = true
      this._notifyPlayState(true)
      this._updateReview()
    }
  }

  private _stopTimer () {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  private _stopReview () {
    this.running = false
    this._stopTimer()
    this._notifyPlayState(false)
    if (this.playTimer) {
      clearTimeout(this.playTimer)
      this.playTimer = null
    }
  }

  _startDelay () {
    if (this._isSlideShowPossible()) {
      this.startDelayTimer = setTimeout(() => {
        this._startReview()
      }, this.settings.startDelay)
    }
  }

  _stopDelay () {
    if (this.startDelayTimer) {
      clearTimeout(this.startDelayTimer)
    }
    this.startDelayTimer = null
  }

  _startInactivityManager () {
    document.body.addEventListener('click', this._inactivityManager)
    document.body.addEventListener('touchstart', this._inactivityManager)
  }

  _stopInactivityManager () {
    document.body.removeEventListener('click', this._inactivityManager)
    document.body.removeEventListener('touchstart', this._inactivityManager)
  }

  _inactivityManager = () => {
    if (this.running) {
      this._stopReview()
    }

    this._stopDelay()

    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer)
      this.inactivityTimer = null
    }

    this.inactivityTimer = setTimeout(() => {
      if (this._isSlideShowPossible()) {
        this._changeRoute(ROUTES.HOME)
      }
    }, this.settings.inactivityTimeout)
  }

  _changeRoute (pathname: string) {
    this.props.navigate(`${pathname.replace('?', '')}${this.props.location.search}`)
  }

  _isSlideShowPossible () {
    const { location } = this.props

    return !this.running
      // @ts-expect-error leave this as is
      && !location?.state?.noSlide
      && !routesWithoutSlideShow.includes(location.pathname)
  }

  _getRemainingTimeout () {
    if (!this.startedTime) {
      return 1
    }

    const elapsedTime = Date.now() - this.startedTime.getTime()
    const currentPageTimeout = this.pages[this.pageIndex].timeout
    const remainingTime = currentPageTimeout - elapsedTime

    return Math.max(remainingTime, this.remainingMinTime)
  }

  static getInstance (props?: RemoteControlProps, socket?: Socket) {
    if (!instance && props && socket) {
      instance = new RemoteControl(props, socket)
    }
    return instance
  }
}
